import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { 
  Sparkles, 
  CheckCircle, 
  Phone, 
  FileText, 
  ArrowRight, 
  Calendar, 
  Lock, 
  ShoppingBag, 
  Loader2, 
  ChevronDown, 
  ChevronLeft,
  ChevronRight,
  Check, 
  Package as PackageIcon, 
  Tag,
  FlaskConical,
  Pill,
  Activity,
  Building2
} from "lucide-react";
import UploadPrescriptionSlider from "../HomePage/Priscription/UploadPrescriptionSlider";
import CallPopup from "../common/CallPopup";
import LoginPopup from "../common/LoginPopup";
import ReplaceCartPopup from "../LabTest/ReplaceCartPopup";
import { toast } from "react-toastify";
import { fetchAllPackagesFromAPI } from "../../services/HomePage/AllPackagesAPI";
import { usePackageCartActions } from "../../hooks/usePackageCartActions";
import FloatingSideQR from "./FloatingSideQR";
import PackageCard from "../LabTest/PackageCard";

export default function LandingPage() {
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart.items || []);
  const [showUpload, setShowUpload] = useState(false);
  const [showCall, setShowCall] = useState(false);
  
  // Dynamic packages from Live API
  const [livePackages, setLivePackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  
  // Package slider ref
  const packageSliderRef = useRef(null);

  const slidePackages = (direction) => {
    if (packageSliderRef.current) {
      const scrollAmount = direction === "left" ? -320 : 320;
      packageSliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };
  
  // Custom Dropdown State
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Form State
  const [bookingName, setBookingName] = useState("");
  const [bookingPhone, setBookingPhone] = useState("");
  const [bookingCity, setBookingCity] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Cart actions integration
  const {
    addPackage,
    showLogin,
    setShowLogin,
    showReplacePopup,
    pendingPackage,
    handleConfirmReplace,
    handleCancelReplace,
  } = usePackageCartActions();

  // Curated fallback packages if API is offline or returns empty
  const defaultPackages = [
    {
      packageId: "smart-plus",
      packageName: "Smart+ Full Body Checkup",
      packageDescription: "Comprehensive Preventive Health Checkup with 68 Vital Tests",
      totalTests: 68,
      packageMRP: 2499,
      packageOfferPrice: 699,
      discount: 72,
      badge: "⭐ Best Value",
      tests: [
        "Complete Blood Count (CBC - 24 parameters)",
        "Lipid Profile (Cholesterol, HDL, LDL, Triglycerides)",
        "Liver Function Test (SGOT, SGPT, Bilirubin, Protein)",
        "Kidney Function Test (Urea, Creatinine, Uric Acid)",
        "Blood Glucose Fasting / HbA1c",
        "Thyroid Profile (TSH Total)",
        "Urine Routine & Microscopic Examination"
      ]
    },
    {
      packageId: "senior-citizen",
      packageName: "Senior Citizen Comprehensive Package",
      packageDescription: "Specialized diagnostics designed for adults aged 45+",
      totalTests: 82,
      packageMRP: 3999,
      packageOfferPrice: 1199,
      discount: 70,
      badge: "👴 Specialized Care",
      tests: [
        "All Smart+ Full Body Tests Included",
        "Vitamin D3 (25-OH) & Vitamin B12",
        "Cardiac Risk Marker (hs-CRP, Homocysteine)",
        "Calcium, Phosphorus & Electrolytes",
        "HbA1c with Average Blood Sugar (eAG)",
        "Complete Iron Deficiency Profile",
        "Free Doctor Tele-Consultation on Report"
      ]
    },
    {
      packageId: "diabetes-heart",
      packageName: "Diabetes & Cardiac Care Package",
      packageDescription: "Complete Metabolic & Heart Health Diagnostic Screen",
      totalTests: 55,
      packageMRP: 2199,
      packageOfferPrice: 549,
      discount: 75,
      badge: "❤️ Heart Care",
      tests: [
        "HbA1c (Glycosylated Hemoglobin)",
        "Fasting Blood Sugar (FBS)",
        "Lipid Profile Full Screen",
        "Renal / Kidney Health (Serum Creatinine)",
        "Urine Microalbumin / Creatinine Ratio",
        "Blood Pressure & BMI Report Assessment"
      ]
    }
  ];

  // Load packages dynamically from database (identical to Lab page)
  useEffect(() => {
    let isMounted = true;
    const loadPackages = async () => {
      try {
        setLoadingPackages(true);
        
        const [pkgRes, labsRes] = await Promise.all([
          axios.get('https://admin.medrphalabs.com/api/Package?id=0').catch(() => ({ data: [] })),
          axios.get('https://admin.medrphalabs.com/api/Lab').catch(() => ({ data: [] }))
        ]);

        let pkgList = [];
        if (Array.isArray(pkgRes.data)) {
          pkgList = pkgRes.data;
        } else if (Array.isArray(pkgRes.data?.data)) {
          pkgList = pkgRes.data.data;
        }

        let labsList = [];
        if (Array.isArray(labsRes.data)) {
          labsList = labsRes.data;
        } else if (Array.isArray(labsRes.data?.data)) {
          labsList = labsRes.data.data;
        }

        // Fallback to MasterAPI if admin API returns empty
        if (pkgList.length === 0) {
          const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://online-tech.in/api').replace(/\/+$/, '');
          const res = await axios.get(`${API_BASE_URL}/MasterAPI/GetTestPackagesAll?pageNumber=1&pageSize=100`).catch(() => ({ data: {} }));
          if (res.data?.succeeded && Array.isArray(res.data?.data)) {
            pkgList = res.data.data;
          }
        }

        if (isMounted) {
          const mappedPackages = pkgList
            .filter(p => p.isActive !== false && p.isActive !== 0 && (p.packageName || p.name))
            .map(pkg => {
              let imgUrl = null;
              if (pkg.image && typeof pkg.image === 'string' && pkg.image.trim()) {
                const clean = pkg.image.trim();
                if (clean.startsWith('http')) {
                  imgUrl = clean;
                } else if (clean.startsWith('PackageImage/') || clean.startsWith('LabImage/') || clean.startsWith('TestImage/')) {
                  imgUrl = `https://admin.medrphalabs.com/${clean}`;
                } else {
                  imgUrl = `https://admin.medrphalabs.com/PackageImage/${clean}`;
                }
              }
              
              const labObj = labsList.find(l => Number(l.id) === Number(pkg.labId));
              const labName = labObj?.name || labObj?.labName || labObj?.lab_name || (pkg.labId === 1 ? 'DGchem Lab' : (pkg.labId === 3 ? 'DOCOPD LAB' : 'Certified Partner Lab'));

              return {
                ...pkg,
                dynamicImageUrl: imgUrl,
                assignedLabName: labName,
              };
            });

          const valid = mappedPackages.length > 0 ? mappedPackages : defaultPackages;
          setLivePackages(valid);
          const firstId = valid[0]?.packageId || valid[0]?.id || valid[0]?.PackageId;
          if (firstId) setSelectedPackageId(firstId);
        }
      } catch (err) {
        console.error("Failed to load live packages for landing page:", err);
        if (isMounted) {
          setLivePackages(defaultPackages);
          setSelectedPackageId(defaultPackages[0].packageId);
        }
      } finally {
        if (isMounted) setLoadingPackages(false);
      }
    };

    loadPackages();
    return () => {
      isMounted = false;
    };
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentSelectedPackage = 
    livePackages.find((p) => String(p.packageId || p.id || p.PackageId) === String(selectedPackageId)) ||
    defaultPackages[0];

  const featuredPackages = livePackages.length >= 3 ? livePackages.slice(0, 6) : defaultPackages;

  const faqs = [
    {
      q: "How does home sample collection work?",
      a: "Our certified DMLT phlebotomist visits your home at your scheduled time slot with temperature-controlled vacuum tubes. Samples are immediately safely transported to our NABL-accredited laboratory."
    },
    {
      q: "When and how will I get my test reports?",
      a: "Digital reports are generated within 6 to 12 hours and sent directly via WhatsApp, SMS, and your secure Medrpha online patient dashboard."
    },
    {
      q: "Is fasting required for full body checkup?",
      a: "Yes, 8 to 10 hours of overnight fasting is recommended for accurate Blood Sugar and Lipid Profile tests. You may drink normal water."
    },
    {
      q: "Are the partner laboratories authentic and certified?",
      a: "All partner labs are 100% NABL and ISO certified with fully automated barcoded analyzers ensuring 99.9% clinical accuracy."
    }
  ];

  // Dynamic Form Submission Flow
  const handleQuickBook = async (e) => {
    e.preventDefault();
    if (!bookingName || !bookingPhone || bookingPhone.length < 10) {
      toast.error("Please provide your full name and a valid 10-digit mobile number");
      return;
    }

    setSubmitting(true);

    try {
      const selectedPkg = currentSelectedPackage;

      // 1. If user is logged in, add package directly to cart
      const userData = JSON.parse(localStorage.getItem("medarphaUser") || "{}");
      if (userData?.userId && selectedPkg) {
        addPackage(selectedPkg);
      }

      toast.success(
        `Thank you ${bookingName}! Your home checkup request for "${selectedPkg.packageName || selectedPkg.name}" has been placed. Our clinical coordinator will call you in 2 minutes.`
      );

      // Reset form
      setBookingName("");
      setBookingPhone("");
      setBookingCity("");
    } catch (err) {
      console.error("Booking submission error:", err);
      toast.error("Booking request could not be processed. Please try again or call our hotline.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDirectPackageBook = (pkg) => {
    addPackage(pkg);
  };

  return (
    <div className="w-full min-h-screen bg-slate-50 font-sans text-slate-800">
      
      {/* 1. HERO CAMPAIGN BANNER - FULL WIDTH & DYNAMIC */}
      <section className="relative overflow-hidden bg-gradient-to-br from-red-50/50 via-white to-slate-50 text-slate-900 py-10 md:py-16 px-4 sm:px-8 lg:px-12 xl:px-16 border-b border-slate-200/80">
        <div className="w-full max-w-[1720px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-14 items-center relative z-10">
          
          {/* Left Hero Content */}
          <div className="lg:col-span-7 space-y-5">
            <div className="inline-flex items-center gap-2 bg-red-100/70 px-4 py-1.5 rounded-full border border-red-200 text-xs font-bold text-red-700 shadow-2xs">
              <Sparkles size={14} className="text-red-600" />
              <span>Special Diagnostic Campaign - Up to 75% OFF</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 leading-tight tracking-tight">
              Book Full Body Checkup <br />
              <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
                With Free Home Sample Pickup
              </span>
            </h1>

            <p className="text-sm md:text-base text-slate-600 leading-relaxed max-w-2xl font-medium">
              Certified NABL Diagnostic Labs • 68+ Vital Parameters • Digital Reports in 6 Hours on WhatsApp • Trusted by 10 Lakh+ Patients Across India.
            </p>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-2 text-xs md:text-sm text-slate-700 font-bold">
                <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" />
                <span>100% NABL Accredited</span>
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm text-slate-700 font-bold">
                <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" />
                <span>Free Home Sample Pickup</span>
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm text-slate-700 font-bold">
                <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" />
                <span>DMLT Certified Phlebotomists</span>
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm text-slate-700 font-bold">
                <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" />
                <span>Free Doctor Tele-Review</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3.5 pt-4">
              <button
                onClick={() => navigate("/all-packages")}
                className="px-7 py-3.5 bg-gradient-to-r from-red-600 to-rose-600 text-white font-black rounded-2xl shadow-md shadow-red-200 hover:from-red-700 hover:to-rose-700 hover:scale-105 transition-all text-xs md:text-sm flex items-center gap-2 cursor-pointer"
              >
                <span>View Packages @ ₹549</span>
                <ArrowRight size={16} />
              </button>

              <button
                onClick={() => navigate("/home")}
                className="px-7 py-3.5 bg-white hover:bg-slate-100 text-slate-800 font-bold rounded-2xl border border-slate-200 shadow-xs transition-all text-xs md:text-sm flex items-center gap-2 cursor-pointer"
              >
                <span>Explore Store & Medicines</span>
              </button>

              <button
                onClick={() => setShowUpload(true)}
                className="px-6 py-3.5 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-2xl border border-red-200 transition-all text-xs md:text-sm flex items-center gap-2 cursor-pointer"
              >
                <FileText size={16} className="text-red-600" />
                <span>Upload Prescription</span>
              </button>
            </div>
          </div>

          {/* Right Hero Quick Booking Form - 100% Dynamic with Custom Apollo Dropdown */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-slate-200/90 text-slate-900">
              <div className="mb-4 text-center">
                <span className="text-[10px] font-black uppercase tracking-wider text-red-700 bg-red-50 px-3 py-1 rounded-full border border-red-100">
                  ⚡ Instant Callback in 2 Mins
                </span>
                <h3 className="text-xl font-black text-slate-900 mt-2">
                  Book Your Home Checkup
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Enter your details to lock discount price today
                </p>
              </div>

              <form onSubmit={handleQuickBook} className="space-y-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Your Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter your full name"
                    value={bookingName}
                    onChange={(e) => setBookingName(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none transition-all font-medium bg-slate-50/50"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Mobile Number *</label>
                  <input
                    type="tel"
                    required
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    value={bookingPhone}
                    onChange={(e) => setBookingPhone(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none transition-all font-medium bg-slate-50/50"
                  />
                </div>

                {/* ATTRACTIVE CUSTOM DOWNWARD-OPENING DROPDOWN */}
                <div className="relative" ref={dropdownRef}>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Select Health Package (Live Database)
                  </label>

                  {loadingPackages ? (
                    <div className="flex items-center gap-2 py-2.5 px-4 bg-slate-50 rounded-xl border text-xs text-slate-500">
                      <Loader2 size={14} className="animate-spin text-red-600" />
                      <span>Loading active packages from server...</span>
                    </div>
                  ) : (
                    <>
                      {/* Dropdown Trigger Button */}
                      <div
                        onClick={() => setIsDropdownOpen((prev) => !prev)}
                        className={`w-full px-3.5 py-2.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-2 bg-slate-50/70 hover:bg-slate-50 ${
                          isDropdownOpen 
                            ? "border-red-500 ring-2 ring-red-100 bg-white" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                            <PackageIcon size={14} />
                          </div>
                          <div className="flex flex-col text-left truncate">
                            <span className="text-xs font-bold text-slate-900 truncate">
                              {currentSelectedPackage?.packageName || currentSelectedPackage?.name || "Select Package"}
                            </span>
                            <span className="text-[10px] text-slate-500 font-medium">
                              {currentSelectedPackage?.totalTests ? `${currentSelectedPackage.totalTests}+ Tests` : "Full Profile"} • <strong className="text-emerald-600 font-bold">₹{currentSelectedPackage?.packageOfferPrice || currentSelectedPackage?.price || 699}</strong>
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-full hidden sm:inline-block">
                            Best Deal
                          </span>
                          <ChevronDown
                            size={16}
                            className={`text-slate-400 transition-transform duration-200 ${
                              isDropdownOpen ? "rotate-180 text-red-600" : ""
                            }`}
                          />
                        </div>
                      </div>

                      {/* Downward-Opening Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 p-2 space-y-1 max-h-64 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-150">
                          <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 mb-1">
                            Available Checkup Packages ({livePackages.length})
                          </div>
                          {livePackages.map((pkg) => {
                            const pId = pkg.packageId || pkg.id || pkg.PackageId;
                            const pName = pkg.packageName || pkg.name || "Health Package";
                            const price = pkg.packageOfferPrice || pkg.offerPrice || pkg.price || 699;
                            const mrp = pkg.packageMRP || pkg.mrp || price * 2;
                            const testsCount = pkg.totalTests || pkg.testsCount || 60;
                            const isSelected = String(pId) === String(selectedPackageId);

                            return (
                              <div
                                key={pId}
                                onClick={() => {
                                  setSelectedPackageId(pId);
                                  setIsDropdownOpen(false);
                                }}
                                className={`p-2.5 rounded-xl cursor-pointer transition-all flex items-center justify-between gap-3 ${
                                  isSelected
                                    ? "bg-red-50/80 border border-red-200 text-red-900 shadow-2xs"
                                    : "hover:bg-slate-50 border border-transparent text-slate-700"
                                }`}
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                                    isSelected ? "bg-red-600 text-white" : "bg-slate-100 text-slate-400"
                                  }`}>
                                    {isSelected ? <Check size={12} /> : null}
                                  </div>
                                  <div className="flex flex-col min-w-0">
                                    <span className="text-xs font-bold truncate">
                                      {pName}
                                    </span>
                                    <span className="text-[10px] text-slate-500 font-medium">
                                      Includes {testsCount}+ Parameters
                                    </span>
                                  </div>
                                </div>

                                <div className="text-right shrink-0">
                                  <div className="text-xs font-black text-emerald-600">
                                    ₹{price}
                                  </div>
                                  {mrp > price && (
                                    <div className="text-[10px] text-slate-400 line-through">
                                      ₹{mrp}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">City / Pincode</label>
                  <input
                    type="text"
                    placeholder="e.g. Delhi, Mumbai, Noida"
                    value={bookingCity}
                    onChange={(e) => setBookingCity(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-red-500 focus:outline-none transition-all font-medium bg-slate-50/50"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white font-black rounded-xl shadow-md shadow-red-200 hover:from-red-700 hover:to-rose-700 transition-all text-xs flex items-center justify-center gap-2 cursor-pointer mt-2 disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      <span>Confirming Slot...</span>
                    </>
                  ) : (
                    <>
                      <Calendar size={15} />
                      <span>Request Free Home Collection</span>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-center gap-2 text-[10px] text-slate-500 font-semibold">
                <Lock size={12} className="text-emerald-600" />
                <span>100% Safe & Confidential • Zero Advance Payment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. QUICK ACCESS CATEGORIES BAR (LABS, MEDICINES, TESTS, PACKAGES) */}
      <section className="bg-white border-b border-slate-200/80 py-7 px-4 sm:px-8 lg:px-12 xl:px-16">
        <div className="w-full max-w-[1720px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider bg-red-50 px-2.5 py-0.5 rounded-full border border-red-100">
                Quick Healthcare Services
              </span>
              <h3 className="text-lg md:text-xl font-black text-slate-900 mt-1 tracking-tight">
                Explore Diagnostics & Medicines
              </h3>
            </div>
            <button
              onClick={() => navigate("/home")}
              className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1 cursor-pointer"
            >
              <span>View All Services</span>
              <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {/* 1. Diagnostic Labs */}
            <div
              onClick={() => navigate("/lab")}
              className="group p-4 rounded-2xl bg-slate-50/70 hover:bg-white border border-slate-200/80 hover:border-amber-300 shadow-2xs hover:shadow-md transition-all duration-200 cursor-pointer flex items-center gap-3.5 transform hover:-translate-y-0.5"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                <Activity size={20} />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs md:text-sm font-black text-slate-900 group-hover:text-amber-600 transition-colors truncate">
                  Diagnostic Labs
                </h4>
                <p className="text-[11px] text-slate-500 truncate font-medium">50+ NABL Verified Labs</p>
              </div>
            </div>

            {/* 2. Buy Medicines */}
            <div
              onClick={() => navigate("/medicine")}
              className="group p-4 rounded-2xl bg-slate-50/70 hover:bg-white border border-slate-200/80 hover:border-emerald-300 shadow-2xs hover:shadow-md transition-all duration-200 cursor-pointer flex items-center gap-3.5 transform hover:-translate-y-0.5"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                <Pill size={20} />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs md:text-sm font-black text-slate-900 group-hover:text-emerald-600 transition-colors truncate">
                  Order Medicines
                </h4>
                <p className="text-[11px] text-slate-500 truncate font-medium">Flat 20% OFF • 2h Delivery</p>
              </div>
            </div>

            {/* 3. Book Lab Tests */}
            <div
              onClick={() => navigate("/all-tests")}
              className="group p-4 rounded-2xl bg-slate-50/70 hover:bg-white border border-slate-200/80 hover:border-blue-300 shadow-2xs hover:shadow-md transition-all duration-200 cursor-pointer flex items-center gap-3.5 transform hover:-translate-y-0.5"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                <FlaskConical size={20} />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs md:text-sm font-black text-slate-900 group-hover:text-blue-600 transition-colors truncate">
                  Book 100+ Tests
                </h4>
                <p className="text-[11px] text-slate-500 truncate font-medium">CBC, Sugar, Thyroid, KFT</p>
              </div>
            </div>

            {/* 4. Full Body Checkups */}
            <div
              onClick={() => navigate("/all-packages")}
              className="group p-4 rounded-2xl bg-slate-50/70 hover:bg-white border border-slate-200/80 hover:border-rose-300 shadow-2xs hover:shadow-md transition-all duration-200 cursor-pointer flex items-center gap-3.5 transform hover:-translate-y-0.5"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 text-white flex items-center justify-center shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                <PackageIcon size={20} />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs md:text-sm font-black text-slate-900 group-hover:text-red-600 transition-colors truncate">
                  Health Packages
                </h4>
                <p className="text-[11px] text-slate-500 truncate font-medium">Up to 75% OFF • 60+ Tests</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. STATS RIBBON - FULL WIDTH */}
      <section className="bg-white border-b border-slate-200/80 py-6 px-4 sm:px-8 lg:px-12 xl:px-16">
        <div className="w-full max-w-[1720px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <p className="text-2xl md:text-3xl font-black text-slate-900">10 Lakh+</p>
            <p className="text-xs text-slate-600 mt-0.5 font-semibold">Satisfied Patients</p>
          </div>
          <div className="border-l border-slate-200">
            <p className="text-2xl md:text-3xl font-black text-emerald-600">100%</p>
            <p className="text-xs text-slate-600 mt-0.5 font-semibold">NABL Certified Labs</p>
          </div>
          <div className="border-t md:border-t-0 md:border-l border-slate-200 pt-3 md:pt-0">
            <p className="text-2xl md:text-3xl font-black text-blue-600">4.8 / 5.0</p>
            <p className="text-xs text-slate-600 mt-0.5 font-semibold">⭐ Google Patient Rating</p>
          </div>
          <div className="border-t md:border-t-0 md:border-l border-slate-200 pt-3 md:pt-0">
            <p className="text-2xl md:text-3xl font-black text-red-600">6 Hours</p>
            <p className="text-xs text-slate-600 mt-0.5 font-semibold">Fast Digital Reports</p>
          </div>
        </div>
      </section>

      {/* 3. POPULAR HEALTH CHECKUP PACKAGES (MATCHING LAB PAGE UI & DYNAMIC SLIDER) */}
      <section className="py-12 md:py-16 px-4 sm:px-8 lg:px-12 xl:px-16 w-full max-w-[1720px] mx-auto">
        <div className="bg-white border border-slate-200/90 rounded-3xl p-5 md:p-8 shadow-xs relative">
          
          {/* Header with Navigation Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-2 h-7 bg-gradient-to-b from-red-600 to-rose-600 rounded-full" />
              <div>
                <h3 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">
                  Popular Health Checkup Packages
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5">
                  Showing {livePackages.length} NABL-accredited health packages with free home sample pickup
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              {/* Slider Arrow Buttons */}
              <button
                onClick={() => slidePackages("left")}
                className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
                title="Slide Left"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => slidePackages("right")}
                className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
                title="Slide Right"
              >
                <ChevronRight size={18} />
              </button>

              <button 
                onClick={() => navigate("/all-packages")}
                className="text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-xl transition-all flex items-center gap-1 ml-1 cursor-pointer"
              >
                <span>View All Packages</span>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {/* Dynamic Package Cards SLIDER */}
          {loadingPackages ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-slate-50 rounded-2xl p-4 border border-slate-200 animate-pulse space-y-3">
                  <div className="h-40 bg-slate-200 rounded-xl w-full" />
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                  <div className="h-9 bg-slate-200 rounded-xl w-full" />
                </div>
              ))}
            </div>
          ) : livePackages.length === 0 ? (
            <div className="bg-slate-50 rounded-2xl p-8 text-center border border-slate-200 my-2">
              <PackageIcon size={36} className="text-slate-300 mx-auto mb-2" />
              <p className="text-xs font-bold text-slate-700">No popular health packages found.</p>
            </div>
          ) : (
            <div 
              ref={packageSliderRef}
              className="flex items-stretch gap-4 overflow-x-auto no-scrollbar pb-3 pt-1 scroll-smooth"
            >
              {livePackages.map((pkg, idx) => {
                const pkgId = pkg.packageId || pkg.id || idx;
                const inCart = cart.some((item) => Number(item.productId) === Number(pkgId) && Number(item.categoryId) === 3);

                return (
                  <PackageCard
                    key={pkgId}
                    pkg={pkg}
                    inCart={inCart}
                    onCartToggle={(p) => addPackage(p)}
                    className="w-[270px] sm:w-[290px] flex-shrink-0"
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 4. FLOATING SMART QR WIDGET (DETECTS IPHONE / ANDROID / PC ON SCAN) */}
      <FloatingSideQR />

      {/* 5. FREQUENTLY ASKED QUESTIONS */}
      <section className="py-12 md:py-16 px-4 sm:px-8 lg:px-12 xl:px-16 bg-white border-t border-slate-200/80">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider bg-red-50 px-3 py-1 rounded-full border border-red-100">
              Got Questions?
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mt-2 tracking-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                <h4 className="text-sm font-bold text-slate-900 mb-2">{faq.q}</h4>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. FLOATING BOTTOM CTA */}
      <div className="fixed bottom-4 left-4 right-4 max-w-lg mx-auto bg-white/95 backdrop-blur-md text-slate-900 p-3 rounded-2xl shadow-xl border border-slate-200/90 z-40 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
            <Phone size={16} />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-900">Need Help Booking?</p>
            <p className="text-[10px] text-slate-500 font-medium">Free Call Consultation</p>
          </div>
        </div>
        <button
          onClick={() => setShowCall(true)}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-xs"
        >
          Call Expert Now
        </button>
      </div>

      {/* POPUPS & CART MODALS */}
      {showUpload && (
        <UploadPrescriptionSlider
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
        />
      )}

      {showCall && (
        <CallPopup
          isOpen={showCall}
          onClose={() => setShowCall(false)}
        />
      )}

      {showLogin && (
        <LoginPopup
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
        />
      )}

      {showReplacePopup && (
        <ReplaceCartPopup
          isOpen={showReplacePopup}
          onConfirm={handleConfirmReplace}
          onCancel={handleCancelReplace}
          packageData={pendingPackage}
        />
      )}
    </div>
  );
}
